import { BrainCircuit, Radar, Shield, Network, Fingerprint, Users, ExternalLink, Zap } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import PageHero from "../components/PageHero";
import CtaBand from "../components/CtaBand";
import { Reveal, SectionHeading, Eyebrow } from "../components/Reveal";

const HERO_IMG = "https://images.unsplash.com/photo-1691435828932-911a7801adfb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHw0fHxjeWJlcnNlY3VyaXR5JTIwbmV0d29ya3xlbnwwfHx8fDE3OTA2MDkxNTZ8MA&ixlib=rb-4.1.0&q=85";
const moduleIcons = [BrainCircuit, Radar, Shield, Network, Fingerprint, Users];

export default function KonectSystems() {
  const { t } = useLang();
  const k = t.konect;

  return (
    <>
      <PageHero
        testid="konect-hero"
        eyebrow={k.eyebrow}
        titleLines={k.title}
        paragraphs={[k.p1, k.p2]}
        image={HERO_IMG}
      />

      {/* KRONOS modules */}
      <section className="py-20 lg:py-28" data-testid="kronos-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeading eyebrow={k.kronosEyebrow} title={k.kronosTitle} />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {k.modules.map((m, i) => {
              const Icon = moduleIcons[i];
              return (
                <Reveal key={m.code} delay={(i % 3) * 0.08} className="h-full">
                  <div
                    className="group relative h-full rounded-lg p-7 bg-obsidian/80 border border-konect/15 hover:border-konect/50 transition-all duration-500 hover:shadow-[0_16px_50px_rgba(255,153,0,0.08)] overflow-hidden"
                    data-testid={`konect-module-${i}`}
                  >
                    <div className="scanlines absolute inset-0 pointer-events-none" aria-hidden="true" />
                    <div className="relative">
                      <div className="flex items-start justify-between">
                        <Icon className="w-6 h-6 text-konect" strokeWidth={1.5} aria-hidden="true" />
                        <span className="font-mono text-[10px] tracking-[0.25em] text-slate-600">
                          MOD {String(i + 1).padStart(2, "0")}
                        </span>
                      </div>
                      <p className="mt-6 font-mono text-xs font-bold tracking-[0.3em] uppercase text-konect">{m.code}</p>
                      <h3 className="mt-2.5 font-display text-lg font-bold text-slate-100">{m.name}</h3>
                      <p className="mt-3 text-sm text-slate-400 leading-relaxed">{m.desc}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-10 glass rounded-lg p-6 flex items-center gap-5 border-cobalt/25" data-testid="konect-ibm-note">
              <Zap className="w-5 h-5 text-cobalt shrink-0" aria-hidden="true" />
              <p className="text-sm text-slate-300">{k.ibmNote}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* on.it Sovereign AI */}
      <section className="relative py-24 border-y border-konect/20 overflow-hidden" data-testid="onit-section">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse 55% 70% at 50% 50%, rgba(255,153,0,0.07), transparent 65%)" }}
          aria-hidden="true"
        />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <Reveal>
            <Eyebrow color="text-konect">{k.onitEyebrow}</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight text-slate-100">
              {k.onitTitle}
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="mt-6 text-slate-300 max-w-2xl mx-auto">{k.onitText}</p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-8 flex flex-wrap justify-center gap-2.5">
              {k.onitTags.map((tag, i) => (
                <span key={i} className="border border-konect/30 bg-konect/5 px-3.5 py-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-konect/90">
                  {tag}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Sectors */}
      <section className="py-24" data-testid="konect-sectors">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeading eyebrow={k.sectorsEyebrow} title={k.sectorsTitle} />
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {k.sectors.map((s, i) => (
              <Reveal key={s} delay={i * 0.04}>
                <div
                  className="border border-sovereign/12 bg-obsidian/60 px-4 py-5 text-center font-mono text-[11px] tracking-[0.18em] uppercase text-slate-300 hover:border-sovereign/40 hover:text-sovereign transition-colors duration-300"
                  data-testid={`konect-sector-${i}`}
                >
                  {s}
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1} className="mt-10">
            <a
              href={k.externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="konect-external-link"
              className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-konect transition-colors duration-300"
            >
              <ExternalLink className="w-4 h-4" aria-hidden="true" />
              {k.external}
            </a>
          </Reveal>
        </div>
      </section>

      <CtaBand title={k.ctaTitle} text={k.p1} btn={k.ctaBtn} />
    </>
  );
}
